import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import { IPageDataBoxType, IAnswerItem, ILimitItem, IGenericObj, IhightLightInfo } from '@/utils/ctype';
import { parseDraftHighlights } from '@/utils/draftHighlights';
import { getExamRequestFailure, settleExamRequest, type ExamRequestFailure } from '@/utils/network/examRequest';
import { getWritingContent, getAnswerList, saveAnswerList, submitAnswer } from '@/api/writing';
import type { ExamRequestOptions } from '@/api/type';
import {fetchWritingAnalysis} from '@/api/ai';
import {renderHightLightPage, commonReducers} from './common';
import { createDraftSaveHealth, recordDraftSaveFailure, type DraftSaveHealth } from './draftSaveHealth';

export interface IListeningTopicListState {
  showNotePanel: boolean;
  pageDataAll: any[];
  pageDataCache: any[];
  pageData: IPageDataBoxType | null;
  recordId: number;
  limits: ILimitItem[];
  answerAll: Array<IAnswerItem| null | undefined>;
  answerMap:IGenericObj<string[]>;
  currentFocus: number;
  pageDataIndex: number;
  loadAnswered: boolean;
  highLightInfo: IhightLightInfo;
  surplus_time: number;
  essay: string;
  big_essay: string;
  is_confirm: number;
  analysisBigEssay: any;
  analysisEssay: any;
  draftSyncedAt: number;
  draftSavePending: boolean;
  draftSaveRequestId?: string;
  draftSaveRequestRecordId?: number;
  draftSaveHealth: DraftSaveHealth;
}

type EssayType = 'essay' | 'big_essay';

const initialState: IListeningTopicListState = {
  showNotePanel: false,
  pageDataCache: [],
  pageDataAll: [],
  pageData: null,
  limits: [],
  answerAll: [],
  answerMap: {},
  currentFocus: 1,
  recordId: 1,
  pageDataIndex: 0,
  loadAnswered: false,
  highLightInfo: {},
  surplus_time: 60 * 60,
  essay: '',
  big_essay: '',
  is_confirm: 0,
  analysisBigEssay: {},
  analysisEssay: {},
  draftSyncedAt: 0,
  draftSavePending: false,
  draftSaveHealth: createDraftSaveHealth(),
};

type ExamLoadRequest = {record_id: number; signal?: AbortSignal;};

export const getAnswerListAsync = createAsyncThunk<any, ExamLoadRequest, {rejectValue: ExamRequestFailure;}>('writing/getAnswerList', async (params, {rejectWithValue, signal}) => {
  try {
    const requestSignals = [params.signal, signal].filter((item): item is AbortSignal => Boolean(item));
    const res = await settleExamRequest<Awaited<ReturnType<typeof getAnswerList>>>(
      requestSignal => getAnswerList({record_id: params.record_id}, {signal: requestSignal}),
      undefined,
      requestSignals,
    );
    if (res.code !== 200) {
      return rejectWithValue(getExamRequestFailure({code: res.code}));
    }
    return res.data;
  } catch (err) {
    return rejectWithValue(getExamRequestFailure(err));
  }
});


export const getWritingAsync = createAsyncThunk<any, ExamLoadRequest, {rejectValue: ExamRequestFailure;}>('writing/getPageData', async (params, {rejectWithValue, signal}) => {
  try {
    const requestSignals = [params.signal, signal].filter((item): item is AbortSignal => Boolean(item));
    const res = await settleExamRequest<Awaited<ReturnType<typeof getWritingContent>>>(
      requestSignal => getWritingContent({record_id: params.record_id}, {signal: requestSignal}),
      undefined,
      requestSignals,
    );
    if (res.code !== 200) {
      return rejectWithValue(getExamRequestFailure({code: res.code}));
    }
    return res.data.list;
  } catch (err) {
    return rejectWithValue(getExamRequestFailure(err));
  }
});

export const saveAnswerListAsync = createAsyncThunk<{id: number; recordId: number;} | undefined, ExamRequestOptions | void, {rejectValue: ExamRequestFailure;}>(
  'writing/saveAnswerListAsync',
  async (params, {rejectWithValue, getState, signal}) => {
    try {
      const state: any = getState();
      const recordId = Number(state.writing.recordId);
      if (params?.recordId !== undefined && Number(params.recordId) !== recordId) {
        return rejectWithValue(getExamRequestFailure({name: 'ConditionError', code: 'EXAM_RECORD_MISMATCH'}));
      }
      const requestSignals = [params?.signal, signal].filter((item): item is AbortSignal => Boolean(item));
      const res = await settleExamRequest<Awaited<ReturnType<typeof saveAnswerList>>>((requestSignal: AbortSignal) => saveAnswerList({
        record_id: recordId,
        essay: state.writing.essay,
        big_essay: state.writing.big_essay,
        mark: state.writing.highLightInfo ? JSON.stringify(state.writing.highLightInfo) : '',
        surplus_time: state.writing.surplus_time,
        is_confirm: state.writing.is_confirm
      }, {signal: requestSignal}), undefined, requestSignals);
      if (res.code !== 200) {
        return rejectWithValue(getExamRequestFailure({code: res.code}));
      }
      const data = res.data as unknown as {id?: number;};
      return {id: Number(data?.id), recordId};
    } catch (err) {
      return rejectWithValue(getExamRequestFailure(err));
    }
  }, {
    condition: (_params, {getState}) => !(getState() as any).writing.draftSavePending,
  });

export const submitAnswerAsync = createAsyncThunk<any, ExamRequestOptions | void, {rejectValue: ExamRequestFailure;}>('writing/submitAnswer', async (params, {rejectWithValue, getState, signal}) => {
  try {
    const recordId = Number((getState() as any).writing.recordId);
    if (params?.recordId !== undefined && Number(params.recordId) !== recordId) {
      return rejectWithValue(getExamRequestFailure({name: 'ConditionError', code: 'EXAM_RECORD_MISMATCH'}));
    }
    const requestSignals = [params?.signal, signal].filter((item): item is AbortSignal => Boolean(item));
    const res = await settleExamRequest<Awaited<ReturnType<typeof submitAnswer>>>((requestSignal: AbortSignal) => submitAnswer({record_id: recordId}, {signal: requestSignal}), undefined, requestSignals);
    if (res.code !== 200) {
      return rejectWithValue(getExamRequestFailure({code: res.code}));
    }
    return res.data;
  } catch (err) {
    return rejectWithValue(getExamRequestFailure(err));
  }
});

export const fetchWritingAnalysisAsync = createAsyncThunk<any, {record_id: number;}>('writing/fetchWritingAnalysis', async (params, {rejectWithValue, getState}) => {
  try {
    const res = await fetchWritingAnalysis(params);
    if (res.code !== 200) {
      return rejectWithValue(null);
    }
    return res.data;
  } catch (err) {
    return rejectWithValue(null);
  }
});

export const writingSlice = createSlice({
  name: 'writing',
  initialState,
  reducers: {
    ...commonReducers,
    resetExamLoadState: (state) => {
      state.showNotePanel = false;
      state.pageDataAll = [];
      state.pageDataCache = [];
      state.pageData = null;
      state.limits = [];
      state.answerAll = [];
      state.answerMap = {};
      state.currentFocus = 1;
      state.pageDataIndex = 0;
      state.loadAnswered = false;
      state.highLightInfo = {};
      state.surplus_time = 60 * 60;
      state.essay = '';
      state.big_essay = '';
      state.is_confirm = 0;
      state.analysisBigEssay = {};
      state.analysisEssay = {};
      state.draftSyncedAt = 0;
      state.draftSavePending = false;
      state.draftSaveRequestId = undefined;
      state.draftSaveRequestRecordId = undefined;
      state.draftSaveHealth = createDraftSaveHealth(state.recordId);
    },
    saveAnswer: (state, {payload}: {payload: {key: string; value: string;};}) => {
      const {key , value} = payload;
      state[key as EssayType] = value;
      if (key === 'essay') {
        state.answerAll[0]!.answer = [value];
      } else {
        state.answerAll[1]!.answer = [value];
      }
    },
    
  },
  extraReducers: (builder) => {
    const dealAnwer = (state: IListeningTopicListState) => {
      // if (state.pageDataAll && state.loadAnswered) {
      //   state.answerAll = state.pageDataAll.map(item=> {
      //     return {id: item.id, answer: ['']};
      //   });
      // }
      
      const curPageData = state.pageDataAll[state.pageDataIndex];
      const highLightObj = state.highLightInfo[state.pageDataIndex];
      if (curPageData && highLightObj) {
        const cloneCurPageData = _.cloneDeep(curPageData);
        renderHightLightPage(highLightObj, cloneCurPageData);
        const pageDataCache = [...state.pageDataCache];
        pageDataCache[state.pageDataIndex] = cloneCurPageData;
        state.pageDataCache = pageDataCache;
      }
    };

   
    builder.addCase(getWritingAsync.fulfilled, (state, action) => {
      if (action.meta.arg.record_id !== state.recordId) return;
      // 预处理
      if (action.payload && action.payload[1]) {
        if (action.payload[0]) {
          action.payload[0].question.frontContent = '';
          action.payload[0].question.afterContent = 'Write at least 150 words.';
        }
        if (action.payload[1]) {
          action.payload[1].question.frontContent = 'Write about the following topic:';
          action.payload[1].question.afterContent = 'Give reasons for your answer and include any relevant examples from your own knowledge and experience. \nWrite at least 250 words.';
        }
      }

      state.pageDataAll = action.payload;
      state.pageDataIndex = 0;
      if (state.pageDataAll) {
        state.pageDataCache = [state.pageDataAll[state.pageDataIndex]];
        state.pageData = state.pageDataAll[state.pageDataIndex];
        state.limits = [{min: 1, max: 1}, {min: 2, max: 2}];
      }
      dealAnwer(state);
    }).addCase(getAnswerListAsync.fulfilled, (state, action) => {
      if (action.meta.arg.record_id !== state.recordId) return;
      state.loadAnswered = true;
      const data = action.payload;
      const {answer, surplus_time, mark, essay, big_essay, is_confirm} = data;
      state.surplus_time = surplus_time || 60 * 60;
      state.highLightInfo = parseDraftHighlights(mark);
      state.answerAll = answer || [{answer: [essay]}, {answer: [big_essay]}];
      state.essay = essay;
      state.big_essay = big_essay;
      state.is_confirm = is_confirm;
      state.draftSyncedAt = Date.now();
      if (action.meta.arg.record_id === state.recordId) state.draftSaveHealth = createDraftSaveHealth(state.recordId);
      dealAnwer(state);
    }).addCase(saveAnswerListAsync.pending, (state, action) => {
      if (action.meta.arg?.recordId === undefined || Number(action.meta.arg.recordId) === state.recordId) {
        state.draftSavePending = true;
        state.draftSaveRequestId = action.meta.requestId;
        state.draftSaveRequestRecordId = state.recordId;
        if (state.draftSaveHealth.recordId !== state.recordId) state.draftSaveHealth = createDraftSaveHealth(state.recordId);
      }
    }).addCase(saveAnswerListAsync.fulfilled, (state, action) => {
      if (state.draftSaveRequestId !== action.meta.requestId) return;
      state.draftSavePending = false;
      state.draftSaveRequestId = undefined;
      state.draftSaveRequestRecordId = undefined;
      if (action.payload?.recordId === state.recordId) {
        state.draftSyncedAt = Date.now();
        state.draftSaveHealth = createDraftSaveHealth(state.recordId);
      }
    })
      .addCase(saveAnswerListAsync.rejected, (state, action) => {
        if (state.draftSaveRequestId !== action.meta.requestId) return;
        const isCurrentRecord = state.draftSaveRequestRecordId === state.recordId;
        state.draftSavePending = false;
        state.draftSaveRequestId = undefined;
        state.draftSaveRequestRecordId = undefined;
        if (isCurrentRecord && state.draftSaveHealth.recordId === state.recordId) recordDraftSaveFailure(state.draftSaveHealth, action.payload || getExamRequestFailure(action.error));
      })
      .addCase(submitAnswerAsync.fulfilled, ()=>{}).addCase(fetchWritingAnalysisAsync.fulfilled, (state, action) => {
        if (action.payload) {
          state.analysisBigEssay = action.payload.big_essay_info;
          state.analysisEssay = action.payload.essay_info;
        }
      });
  },
});


export const {toggleShowNotePanel, setPanelDataByIndex, setFocusIndex, saveAnswer,
  updateTimeState, updateRecordId, showDialog, hideDialog, saveHighlightData,
  clearHightLightInfo, updatePanelInfo, deleteNote, resetExamLoadState} = writingSlice.actions;
